import { useState } from 'react'
import { Minus, Plus, Scaling } from 'lucide-react'
import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEP, readZoom, setZoom } from '@/lib/ui-zoom'

/**
 * Per-device UI scale control. Shrinks the whole POS so it fits a smaller
 * tablet (e.g. a KDS screen) without horizontal scroll. Capped at 100% — we
 * only ever shrink — and flexible on the low end in 5% steps. localStorage-
 * only (see lib/ui-zoom.ts), so it's intentionally NOT in the server-synced
 * theme. The centre button shows the current % and resets to 100% on tap.
 *
 * Rendered as plain buttons (not menu items) so it can live inside the Radix
 * user-menu dropdown without the dropdown closing on each adjustment.
 */
export function ZoomControl({ showLabel = true }: { showLabel?: boolean }) {
  const [zoom, setZoomState] = useState<number>(() => readZoom())
  const pct = Math.round(zoom * 100)
  const change = (next: number) => setZoomState(setZoom(next))

  return (
    <div className={showLabel ? 'px-2 py-1.5' : ''}>
      {showLabel && (
        <p className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Display size
        </p>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => change(zoom - ZOOM_STEP)}
          disabled={zoom <= ZOOM_MIN}
          aria-label="Smaller"
          className="grid h-9 w-9 place-items-center rounded-md border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <Minus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => change(ZOOM_MAX)}
          disabled={zoom === ZOOM_MAX}
          title="Reset to 100%"
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-sm font-medium tabular-nums transition-colors hover:bg-muted disabled:hover:bg-transparent"
        >
          <Scaling className="h-3.5 w-3.5 opacity-60" />
          {pct}%
        </button>
        <button
          type="button"
          onClick={() => change(zoom + ZOOM_STEP)}
          disabled={zoom >= ZOOM_MAX}
          aria-label="Larger"
          className="grid h-9 w-9 place-items-center rounded-md border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
